/**
 * Sync de l'état du filter avec l'URL (`?q=...&pmin=...&page=2&sort=...`).
 *
 * Activé via attribut `sync-query-params="true"` sur `<smootify-search-discovery>`
 * (convention Smootify). Sans cet attribut → pas de write/read URL.
 *
 * Names de query params utilisés (compat partage URL) :
 *   - `q`    : search text
 *   - `pmin` : price min
 *   - `pmax` : price max
 *   - `page` : pagination current page (1-indexed)
 *   - `sort` : tri (price_asc, price_desc, created_dsc, title_asc, etc.)
 *   - `cat`  : collections handles (multi-value, séparé par virgule)
 *   - `tag`  : tags (multi-value, séparé par virgule)
 *
 * Flow :
 *   1. Au boot (avant 1er applyFilters) : `restoreFromUrl()` lit URLSearchParams
 *      et pré-remplit les inputs (search, prix, page, sort, checkbox facets).
 *   2. Après chaque applyFilters réussie : `writeToUrl()` met à jour l'URL via
 *      `history.replaceState` (pas pushState : on ne veut pas polluer l'historique).
 */

export interface UrlFilterState {
  q: string;
  pmin: string;
  pmax: string;
  page: string;
  sort: string;
  cat: string[];
  tag: string[];
}

function isSyncEnabled(doc: Document = document): boolean {
  const wrapper = doc.querySelector("smootify-search-discovery");
  if (!wrapper) return false;
  const v = wrapper.getAttribute("sync-query-params");
  return v === "true" || v === "";
}

export function readFromUrl(): UrlFilterState {
  if (typeof window === "undefined") {
    return { q: "", pmin: "", pmax: "", page: "", sort: "", cat: [], tag: [] };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    q: params.get("q") ?? "",
    pmin: params.get("pmin") ?? "",
    pmax: params.get("pmax") ?? "",
    page: params.get("page") ?? "",
    sort: params.get("sort") ?? "",
    cat: (params.get("cat") ?? "").split(",").filter((s) => s.length > 0),
    tag: (params.get("tag") ?? "").split(",").filter((s) => s.length > 0),
  };
}

/**
 * Pré-remplit les inputs DOM depuis l'URL. Appelé au boot AVANT 1er applyFilters.
 * No-op si `sync-query-params` n'est pas activé.
 */
export function restoreFromUrl(doc: Document = document): void {
  if (!isSyncEnabled(doc)) return;
  const state = readFromUrl();

  // Search input
  if (state.q) {
    const searchInput = doc.querySelector<HTMLInputElement>(
      'input[filter-search], input.search-field, input[name^="Search"]',
    );
    if (searchInput) searchInput.value = state.q;
  }

  // Price inputs (number) + ranges
  if (state.pmin) {
    const fromN = doc.querySelector<HTMLInputElement>('[filter-price="from"]');
    if (fromN) fromN.value = state.pmin;
    // Sync range input
    const ranges = doc.querySelectorAll<HTMLInputElement>(
      'filter-price input[type="range"], .range-containers input[type="range"]',
    );
    if (ranges[0]) ranges[0].value = state.pmin;
  }
  if (state.pmax) {
    const toN = doc.querySelector<HTMLInputElement>('[filter-price="to"]');
    if (toN) toN.value = state.pmax;
    const ranges = doc.querySelectorAll<HTMLInputElement>(
      'filter-price input[type="range"], .range-containers input[type="range"]',
    );
    if (ranges[1]) ranges[1].value = state.pmax;
  }

  // Sort
  if (state.sort) {
    const sortEl = doc.querySelector<HTMLSelectElement | HTMLInputElement>(
      'select[filter-sort], select[name="sort"]',
    );
    if (sortEl) sortEl.value = state.sort;
  }

  // Facets : check les checkboxes correspondantes
  state.cat.forEach((c) => {
    const cb = doc.querySelector<HTMLInputElement>(
      `input[filter-collection="${cssEscape(c)}"]`,
    );
    if (cb) cb.checked = true;
  });
  state.tag.forEach((t) => {
    const cb = doc.querySelector<HTMLInputElement>(
      `input[filter-tag="${cssEscape(t)}"]`,
    );
    if (cb) cb.checked = true;
  });
}

/**
 * Reflète un état filter dans l'URL via history.replaceState.
 * No-op si `sync-query-params` n'est pas activé.
 */
export function writeToUrl(
  state: {
    search: string;
    priceMin: number | null;
    priceMax: number | null;
    sort: string;
    collections: string[];
    tags: string[];
    page: number;
    rangeMin?: number | null;
    rangeMax?: number | null;
  },
  doc: Document = document,
): void {
  if (!isSyncEnabled(doc)) return;
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);

  setOrDelete(params, "q", state.search);
  setOrDelete(
    params,
    "pmin",
    state.priceMin != null && state.priceMin > (state.rangeMin ?? 0)
      ? String(state.priceMin)
      : "",
  );
  setOrDelete(
    params,
    "pmax",
    state.priceMax != null && state.priceMax < (state.rangeMax ?? Infinity)
      ? String(state.priceMax)
      : "",
  );
  setOrDelete(params, "page", state.page > 1 ? String(state.page) : "");
  setOrDelete(params, "sort", state.sort && state.sort !== "relevance" ? state.sort : "");
  setOrDelete(params, "cat", state.collections.length > 0 ? state.collections.join(",") : "");
  setOrDelete(params, "tag", state.tags.length > 0 ? state.tags.join(",") : "");

  const qs = params.toString();
  const newUrl = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
  try {
    window.history.replaceState({}, "", newUrl);
  } catch {
    /* security errors silenced (sandboxed iframes) */
  }
}

function setOrDelete(params: URLSearchParams, key: string, value: string): void {
  if (value && value.length > 0) params.set(key, value);
  else params.delete(key);
}

function cssEscape(s: string): string {
  // Escape pour usage dans selector CSS attribute value
  return s.replace(/(["\\])/g, "\\$1");
}
